export interface KidsProduct {
  id: string;
  name: string;
  category: 'dresses' | 'dungaree' | 'jacket' | 'kurta' | 'lehanga' | 'night_suits' | 'tshirt' | 'tracksuit';
  price: number;
  description: string;
  imageUrl: string;
  availableSizes: string[];
  availableColors: string[];
}

// Generate a random price between 400-1000, weighted mostly below 600
const generatePrice = (_category: string, index: number): number => {
  const seed = (index * 29 + 7) % 100;
  if (seed < 55) return 400 + (seed % 10) * 20;
  if (seed < 80) return 580 + (seed % 8) * 25;
  return 760 + (seed % 10) * 24;
};

// Helper function to generate description based on name and category
const generateDescription = (name: string, category: string): string => {
  const categoryDescriptions: Record<string, string> = {
    'dresses': 'Adorable dress perfect for special occasions and everyday wear',
    'dungaree': 'Cute and comfortable dungarees for playful kids',
    'jacket': 'Warm and stylish jackets for kids in various weather conditions',
    'kurta': 'Traditional kurta sets for festive occasions',
    'lehanga': 'Beautiful lehenga sets for special celebrations',
    'night_suits': 'Comfortable night suits for peaceful sleep',
    'tshirt': 'Fun and colorful t-shirts for casual wear',
    'tracksuit': 'Sporty tracksuits for active kids'
  };
  
  const baseDesc = categoryDescriptions[category] || 'Cute kids fashion item';
  
  // Add some keywords based on the name
  if (name.toLowerCase().includes('party')) return `Party wear ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('cartoon')) return `Cartoon themed ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('floral')) return `Floral print ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('embroidered')) return `Embroidered ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('designer')) return `Designer ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('princess')) return `Princess style ${baseDesc.toLowerCase()}`;
  if (name.toLowerCase().includes('animal')) return `Animal themed ${baseDesc.toLowerCase()}`;
  
  return baseDesc;
};

// Helper function to generate sizes based on category
const generateSizes = (category: string): string[] => {
  // Return 3-4 size options based on category
  if (category === 'dresses' || category === 'lehanga') {
    return ['3-4 Years', '4-5 Years', '5-6 Years', '6-7 Years'];
  } else if (category === 'tshirt' || category === 'night_suits') {
    return ['2-3 Years', '3-4 Years', '4-5 Years', '5-6 Years', '6-7 Years'];
  } else {
    return ['4-5 Years', '5-6 Years', '6-7 Years', '7-8 Years'];
  }
};

// Helper function to generate colors based on category and index
const generateColors = (category: string, index: number): string[] => {
  const colorPools: Record<string, string[][]> = {
    'dresses': [
      ['Pink', 'White', 'Purple'],
      ['Blue', 'Yellow', 'Green'],
      ['Red', 'Pink', 'White'],
      ['Multicolor', 'Rainbow', 'Pastel'],
      ['Pink', 'Purple', 'Lavender']
    ],
    'dungaree': [
      ['Blue', 'Denim', 'Gray'],
      ['Pink', 'Purple', 'Lavender'],
      ['Green', 'Yellow', 'Brown'],
      ['Multicolor', 'Printed', 'Patterned'],
      ['Blue', 'Red', 'Navy']
    ],
    'jacket': [
      ['Blue', 'Red', 'Black'],
      ['Pink', 'Purple', 'White'],
      ['Green', 'Yellow', 'Brown'],
      ['Gray', 'Navy', 'Burgundy'],
      ['Multicolor', 'Printed', 'Cartoon']
    ],
    'kurta': [
      ['White', 'Blue', 'Red'],
      ['Green', 'Yellow', 'Orange'],
      ['Maroon', 'Gold', 'Silver'],
      ['Cream', 'Beige', 'Brown'],
      ['Multicolor', 'Printed', 'Embroidered']
    ],
    'lehanga': [
      ['Pink', 'Red', 'Maroon'],
      ['Blue', 'Green', 'Purple'],
      ['Gold', 'Silver', 'Multicolor'],
      ['White', 'Cream', 'Pastel'],
      ['Red', 'Gold', 'Maroon']
    ],
    'night_suits': [
      ['Blue', 'Pink', 'White'],
      ['Gray', 'Black', 'Navy'],
      ['Pastel Blue', 'Pastel Pink', 'Pastel Green'],
      ['Printed', 'Cartoon', 'Animal'],
      ['Comfort Colors', 'Soft Tones']
    ],
    'tshirt': [
      ['Blue', 'Red', 'Green'],
      ['Black', 'White', 'Gray'],
      ['Yellow', 'Orange', 'Pink'],
      ['Multicolor', 'Printed', 'Cartoon'],
      ['Striped', 'Polka Dot', 'Patterned']
    ],
    'tracksuit': [
      ['Blue', 'Black', 'Gray'],
      ['Red', 'Green', 'Yellow'],
      ['Pink', 'Purple', 'Lavender'],
      ['Printed', 'Sports', 'Team Colors'],
      ['Navy', 'Burgundy', 'Olive']
    ]
  };
  
  const pool = colorPools[category] || [['Blue', 'Red', 'Green']];
  return pool[index % pool.length];
};

// Image data for each category
const categoryImages: Record<string, string[]> = {
  'dresses': [
    'Bow Detail Dress.jpg',
    'Cape Style Dress (2).jpg',
    'Cartoon Printed Dress.jpg',
    'Checked Shirt Dress.jpg',
    'Cotton Summer Dress.jpg',
    'Denim Pinafore Dress.jpg',
    'Embroidered Net Dress.jpg',
    'Floral Party Dress.jpg',
    'girls_frock_dress.jpg',
    'girls_party_dress.jpg',
    'Girls_Tutu_Dress.jpg',
    'High-Low Hem Dress (2).jpg',
    'Kids_Princess_Gown.png',
    'Layered Ruffle Dress.jpg',
    'Off-Shoulder Party Dress.jpg',
    'Polka Dot Dress.jpg',
    'Princess Tulle Dress.jpg',
    'Rainbow Stripe Dress.jpg',
    'Sailor Collar Dress.jpg',
    'Sequin Party Dress.jpg',
    'Smocked Bodice Dress.jpg',
    'Tiered Maxi Dress.jpg',
    'Velvet Party Dress (2).jpg'
  ],
  'dungaree': [
    'Animal Face Dungarees.jpg',
    'Bib Style Baby Dungarees.jpg',
    'Cartoon Character Dungarees.jpg',
    'Corduroy Winter Dungarees.jpg',
    'corduroy_dungaree.jpg',
    'Dinosaur Theme Dungarees.jpg',
    'Floral Garden Dungarees.jpg',
    'kids_denim_dungaree.jpg',
    'Paint Splash Dungarees.jpg',
    'Patchwork Designer Dungarees.jpg',
    'Ruffle Strap Dungarees (Girls).jpg',
    'Short-Length Summer Dungarees.jpg'
  ],
  'jacket': [
    'Bomber Jacket for Kids.jpg',
    'Boys_Denim_Jacket.jpg',
    'Cartoon Print Jacket.jpg',
    'Cartoon Print Tracksuit.jpg',
    'Corduroy Jacket for Kids.jpg',
    'Faux Fur Jacket.jpg',
    'Fleece Zip-Up Jacket.jpg',
    'Hooded Puffer Jacket.jpg',
    'Knitted Sweater Jacket.jpg',
    'Parka Jacket for Kids.jpg',
    'Reversible Jacket.jpg',
    'Sherpa Lined Jacket (2).jpg',
    'Varsity Jacket for Kids.jpg',
    'Windbreaker Jacket (2).jpg',
    'Zipper Jacket Tracksuit.jpg'
  ],
  'kurta': [
    'Anarkali Kurta Set (Girls).jpg',
    'Angrakha Style Kurta Set.jpg',
    'Bandhani Print Kurta Set.jpg',
    'boys_festive_kurta_set.jpg',
    'Cotton Casual Kurta Set.jpg',
    'Dhoti Style Kurta Set.jpg',
    'Embroidered Festive Kurta Set.jpg',
    'Floral Printed Kurta Set (Kids).jpg',
    'Handloom Kurta Set (Kids).jpg',
    'Ikat Print Kurta Set.jpg',
    'kids_ethnic_kurta.jpg',
    'Layered Kurta Set (Kids).jpg',
    'Linen Kurta Set (Kids).jpg',
    'Mirror Work Kurta Set (2).jpg',
    'Pathani Style Kurta Set.jpg',
    'Peplum Kurta Set (Girls).jpg',
    'Printed Nehru Jacket Kurta Set.jpg',
    'Silk Party Wear Kurta Set.jpg',
    'Zari Work Kurta Set.jpg'
  ],
  'lehanga': [
    'Asymmetrical Lehenga Set.jpg',
    'Cotton Festive Lehenga.jpg',
    'Ethnic Lehenga Dress.jpg',
    'Floral Printed Lehenga Set.jpg',
    'girls_festive_lehenga.jpeg',
    'girls_lehenga_choli.jpg',
    'Hand Embroidered Designer Lehenga.jpg',
    'Jacket Style Lehenga.jpg',
    'Lehenga with Cape Dupatta.jpg',
    'Lehenga with Dhoti Pants.jpg',
    'Ombre Dyed Lehenga.jpg',
    'Peplum Style Lehenga Set.jpg',
    'Ruffle Layered Lehenga.jpg',
    'Sequin Party Wear Lehenga.jpg',
    'Silk Traditional Lehenga.jpg',
    'Velvet Festive Lehenga.jpg'
  ],
  'night_suits': [
    'Animal Print Sleepwear Set.jpg',
    'Button-Down Classic Nightwear Set.jpg',
    'Candy Print Cute Night Suit.jpg',
    'Cartoon Printed Cotton Night Suit.jpg',
    'Cute Teddy Print Night Suit.jpg',
    'Fruit Print Summer Night Set.jpg',
    'Glow-in-the-Dark Star Pajamas.jpg',
    'Kids_Night_Suit_Set.jpg',
    'Ocean Theme Night Suit.jpg',
    'Panda Theme Night Suit.jpg',
    'Printed Matching Tracksuit.jpg',
    'Rainbow Cotton Night Suit.jpg',
    'Short Sleeve Summer Sleep Set.jpg',
    'Superhero Theme Nightwear Set.jpg'
  ],
  'tshirt': [
    'Animal Face T-Shirt.jpg',
    'boys_casual_tshirt.jpg',
    'Candy Print T-Shirt.jpg',
    'Cartoon Print T-Shirt.jpg',
    'Cupcake Print T-Shirt.jpg',
    'Dinosaur Print T-Shirt.jpg',
    'Fruit Print T-Shirt.jpg',
    'Gamer Print T-Shirt.jpg',
    'Ice Cream Print T-Shirt.jpg',
    'Jungle Safari T-Shirt.jpg',
    'Music Vibe T-Shirt.jpg',
    'Ocean Theme T-Shirt.jpg',
    'Paint Splash T-Shirt.jpg',
    'Rainbow Graphic T-Shirt.jpg',
    'Space Explorer T-Shirt.jpg',
    'Sports Graphic T-Shirt.jpg',
    'Star Pattern T-Shirt.jpg',
    'Superhero Theme T-Shirt.jpg',
    'Vehicle Print T-Shirt.jpg'
  ],
  'tracksuit': [
    'Camouflage Tracksuit.jpg',
    'Dinosaur Theme Tracksuit.jpg',
    'Emoji Print Tracksuit.jpg',
    'Graphic Logo Tracksuit.jpg',
    'Kids_Winter_Tracksuit.jpg',
    'Neon Active Tracksuit.jpg',
    'Soft Fleece Winter Pajama Set.jpg',
    'Sweatshirt Jogger Tracksuit.jpg',
    'Tie-Dye Tracksuit.jpg'
  ]
};

// Map category keys to actual folder names on disk (case-sensitive for S3)
const categoryFolderMap: Record<string, string> = {
  'dresses': 'Dresses',
  'dungaree': 'Dungaree',
  'jacket': 'jacket',
  'kurta': 'kurta',
  'lehanga': 'lehanga',
  'night_suits': 'night_suits',
  'tshirt': 'T-Shirt',
  'tracksuit': 'tracksuit'
};

// Generate product data for ALL images
const generateAllKidsProducts = (): KidsProduct[] => {
  const products: KidsProduct[] = [];
  let productId = 1;

  Object.entries(categoryImages).forEach(([category, images]) => {
    images.forEach((imageName, index) => {
      // Clean up the name for display
      const cleanName = imageName
        .replace(/\.[^/.]+$/, '') // Remove extension
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/\([^)]*\)/g, '') // Remove parentheses content
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim()
        .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letters
      
      products.push({
        id: `k${productId++}`,
        name: cleanName,
        category: category as 'dresses' | 'dungaree' | 'jacket' | 'kurta' | 'lehanga' | 'night_suits' | 'tshirt' | 'tracksuit',
        price: generatePrice(category, index),
        description: generateDescription(cleanName, category),
        imageUrl: `/assets/images/kids/${categoryFolderMap[category] || category}/${imageName}`,
        availableSizes: generateSizes(category),
        availableColors: generateColors(category, index)
      });
    });
  });

  return products;
};

// Generate ALL kids products
export const kidsProducts: KidsProduct[] = generateAllKidsProducts();

// Get all products
export const getAllKidsProducts = (): KidsProduct[] => {
  return kidsProducts;
};

// Get products by category
export const getKidsProductsByCategory = (category: string): KidsProduct[] => {
  if (category === 'all') {
    return kidsProducts;
  }
  return kidsProducts.filter(product => product.category === category);
};

// Get product by ID
export const getKidsProductById = (id: string): KidsProduct | undefined => {
  return kidsProducts.find(product => product.id === id);
};

// Get all categories
export const getAllKidsCategories = (): string[] => {
  const categories = kidsProducts.map(product => product.category);
  return ['all', ...Array.from(new Set(categories))];
};

// Get category display name
export const getKidsCategoryDisplayName = (category: string): string => {
  const displayNames: Record<string, string> = {
    'all': 'All Products',
    'dresses': 'Dresses',
    'dungaree': 'Dungarees',
    'jacket': 'Jackets',
    'kurta': 'Kurtas',
    'lehanga': 'Lehengas',
    'night_suits': 'Night Suits',
    'tshirt': 'T-Shirts',
    'tracksuit': 'Tracksuits'
  };
  return displayNames[category] || category;
};

// Get products count by category
export const getKidsProductsCountByCategory = (): Record<string, number> => {
  const counts: Record<string, number> = {
    'all': kidsProducts.length
  };
  
  kidsProducts.forEach(product => {
    counts[product.category] = (counts[product.category] || 0) + 1;
  });
  
  return counts;
};